import React, { useState, useMemo } from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { MdAdd, MdClose, MdKeyboardArrowDown } from "react-icons/md";
import { FaRegFileAlt } from "react-icons/fa";
import { Combobox } from "@headlessui/react";
import Input from "../ui/Input";
import useRead from "../../hooks/useRead";

const ExemptionsForm = ({ control, isPOI = false }) => {
  const {
    fields: exemptionFields,
    append: appendExemption,
    remove: removeExemption,
  } = useFieldArray({
    control,
    name: "exemptions_80d",
  });

  // Each exemption instance can have its own query state for Combobox search
  const [queries, setQueries] = useState({});

  // API call to fetch 80D exemptions
  const { list: exemptionsList } = useRead({
    url: "tds/list-exemption-80D",
    initialData: {
      page_number: 0,
      limit: 100,
      search_query: ""
    },
    method: "GET"
  });

  // Transform API data to match existing structure
  const exemptionOptions = useMemo(() => {
    if (!exemptionsList?.data?.data) return [];
    
    return exemptionsList.data.data.map(item => ({
      label: item.label,
      value: item.exemption_80d_id,
      category: "80D", // All exemptions are 80D category
    }));
  }, [exemptionsList?.data?.data]);

  const handleQueryChange = (index, query) => {
    setQueries((prev) => ({ ...prev, [index]: query }));
  };

  const addExemption = () => {
    const newExemption = {
      exemption_80d_id: "",
      amount: "0",
    };
    
    if (isPOI) {
      newExemption.actual_amount = "0";
    }
    
    appendExemption(newExemption);
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Form Header */}
      <div className="flex items-center gap-3 p-4 bg-white border-b border-gray-200">
        <FaRegFileAlt className="w-5 h-5 text-gray-600" />
        <span className="text-base font-medium text-gray-900">
          80D Exemptions
        </span>
      </div>
      {/* Note below header, as per design */}
      <div className="px-6 pt-4 pb-2">
        <span className="text-sm text-gray-600 font-medium">
          Note: This section contains Mediclaim policies for yourself, your
          children, spouse and parents. The maximum limit for this section is
          ₹1,00,000.00
        </span>
      </div>
      {/* Form Content */}
      <div className="px-6 py-6 space-y-8 bg-white">
        {exemptionFields.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm mb-4">
              No exemptions added yet
            </p>
          </div>
        ) : (
          exemptionFields.map((field, index) => {
            const query = queries[index] || "";
            const filtered = exemptionOptions.filter((option) =>
              option.label.toLowerCase().includes(query.toLowerCase())
            );
            const grouped = filtered.reduce((acc, option) => {
              if (!acc[option.category]) acc[option.category] = [];
              acc[option.category].push(option);
              return acc;
            }, {});

            return (
              <div key={field.id} className="space-y-4 relative">
                {/* Exemption header with close button */}
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => removeExemption(index)}
                    className="text-gray-400 hover:text-red-500"
                    aria-label="Remove exemption"
                  >
                    <MdClose className="w-5 h-5" />
                  </button>
                </div>

                {/* Form fields layout based on isPOI */}
                {isPOI ? (
                  // POI Mode: 3-column layout aligned with sticky header
                  <div className="space-y-6">
                    {/* Exemption Type Row */}
                    <div className="flex w-full gap-4">
                      <div className="w-1/3 flex items-center">
                        <label className="text-sm font-medium text-gray-900">
                          Exemption Type
                        </label>
                      </div>
                      <div className="w-2/3">
                        <Controller
                          control={control}
                          name={`exemptions_80d.${index}.exemption_80d_id`}
                          rules={{ required: "Exemption type is required" }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <div className="relative">
                              <Combobox
                                value={
                                  exemptionOptions.find((o) => o.value === value) ||
                                  null
                                }
                                onChange={(selected) => onChange(selected.value)}
                              >
                                <div>
                                  <Combobox.Button className="w-full flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-9 bg-white justify-between">
                                    <span className="truncate text-left">
                                      {exemptionOptions.find((o) => o.value === value)
                                        ?.label || "Select exemption type"}
                                    </span>
                                    <MdKeyboardArrowDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                  </Combobox.Button>
                                </div>
                                <Combobox.Options className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {/* Search bar */}
                                  <div className="px-2 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
                                    <input
                                      type="text"
                                      className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Search..."
                                      value={query}
                                      onChange={(e) =>
                                        handleQueryChange(index, e.target.value)
                                      }
                                      autoFocus
                                    />
                                  </div>
                                  <div className="max-h-48 overflow-auto">
                                    {filtered.length === 0 ? (
                                      <div className="px-4 py-2 text-gray-500">
                                        No options found.
                                      </div>
                                    ) : (
                                      Object.entries(grouped).map(
                                        ([category, options]) => (
                                          <div key={category}>
                                            <div className="px-4 py-1 text-xs font-bold text-gray-800 bg-white">
                                              {category}
                                            </div>
                                            {options.map((option) => (
                                              <Combobox.Option
                                                key={option.value}
                                                value={option}
                                                className={({ active, selected }) =>
                                                  [
                                                    "cursor-pointer select-none relative py-2 pl-6 pr-4",
                                                    active || selected
                                                      ? "bg-blue-500 text-white"
                                                      : "text-gray-900",
                                                  ].join(" ")
                                                }
                                              >
                                                {({ selected }) => (
                                                  <span
                                                    className={[
                                                      "block",
                                                      selected
                                                        ? "font-semibold"
                                                        : "font-normal",
                                                    ].join(" ")}
                                                  >
                                                    {option.label}
                                                  </span>
                                                )}
                                              </Combobox.Option>
                                            ))}
                                          </div>
                                        )
                                      )
                                    )}
                                  </div>
                                </Combobox.Options>
                              </Combobox>
                              {error && (
                                <small className="text-red-600 font-medium text-sm">
                                  {error.message}
                                </small>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* Amount Row */}
                    <div className="flex w-full gap-4">
                      <div className="w-1/3 flex items-center">
                        <label className="text-sm font-medium text-gray-900">
                          Amount
                        </label>
                      </div>
                      <div className="w-1/3 flex justify-center">
                        <div className="w-full max-w-xs">
                          <Input
                            control={control}
                            name={`exemptions_80d.${index}.amount`}
                            type="number"
                            isCurrency={true}
                            placeholder="100000"
                            isPositiveNumber={true}
                            disabled={true}
                            hideLabel={true}
                          />
                        </div>
                      </div>
                      <div className="w-1/3 flex justify-center">
                        <div className="w-full max-w-xs">
                          <Input
                            control={control}
                            name={`exemptions_80d.${index}.actual_amount`}
                            type="number"
                            isCurrency={true}
                            placeholder="100000"
                            isPositiveNumber={true}
                            rules={{ required: "Actual amount is required" }}
                            hideLabel={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Declaration Mode: 2-column layout with separate labels
                  <div className="space-y-6">
                    {/* Exemption Type Row */}
                    <div className="flex w-full gap-4">
                      <div className="w-3/5 flex items-center">
                        <label className="text-sm font-medium text-gray-900">
                          Exemption Type
                        </label>
                      </div>
                      <div className="w-2/5">
                        <Controller
                          control={control}
                          name={`exemptions_80d.${index}.exemption_80d_id`}
                          rules={{ required: "Exemption type is required" }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <div className="relative">
                              <Combobox
                                value={
                                  exemptionOptions.find((o) => o.value === value) ||
                                  null
                                }
                                onChange={(selected) => onChange(selected.value)}
                              >
                                <div>
                                  <Combobox.Button className="w-full flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-9 bg-white justify-between">
                                    <span className="truncate text-left">
                                      {exemptionOptions.find((o) => o.value === value)
                                        ?.label || "Select exemption type"}
                                    </span>
                                    <MdKeyboardArrowDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                  </Combobox.Button>
                                </div>
                                <Combobox.Options className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {/* Search bar */}
                                  <div className="px-2 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
                                    <input
                                      type="text"
                                      className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Search..."
                                      value={query}
                                      onChange={(e) =>
                                        handleQueryChange(index, e.target.value)
                                      }
                                      autoFocus
                                    />
                                  </div>
                                  <div className="max-h-48 overflow-auto">
                                    {filtered.length === 0 ? (
                                      <div className="px-4 py-2 text-gray-500">
                                        No options found.
                                      </div>
                                    ) : (
                                      Object.entries(grouped).map(
                                        ([category, options]) => (
                                          <div key={category}>
                                            <div className="px-4 py-1 text-xs font-bold text-gray-800 bg-white">
                                              {category}
                                            </div>
                                            {options.map((option) => (
                                              <Combobox.Option
                                                key={option.value}
                                                value={option}
                                                className={({ active, selected }) =>
                                                  [
                                                    "cursor-pointer select-none relative py-2 pl-6 pr-4",
                                                    active || selected
                                                      ? "bg-blue-500 text-white"
                                                      : "text-gray-900",
                                                  ].join(" ")
                                                }
                                              >
                                                {({ selected }) => (
                                                  <span
                                                    className={[
                                                      "block",
                                                      selected
                                                        ? "font-semibold"
                                                        : "font-normal",
                                                    ].join(" ")}
                                                  >
                                                    {option.label}
                                                  </span>
                                                )}
                                              </Combobox.Option>
                                            ))}
                                          </div>
                                        )
                                      )
                                    )}
                                  </div>
                                </Combobox.Options>
                              </Combobox>
                              {error && (
                                <small className="text-red-600 font-medium text-sm">
                                  {error.message}
                                </small>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* Amount Row */}
                    <div className="flex w-full gap-4">
                      <div className="w-3/5 flex items-center">
                        <label className="text-sm font-medium text-gray-900">
                          Amount
                        </label>
                      </div>
                      <div className="w-2/5 flex justify-end">
                        <div className="w-full max-w-xs">
                          <Input
                            control={control}
                            name={`exemptions_80d.${index}.amount`}
                            type="number"
                            isCurrency={true}
                            placeholder="100000"
                            isPositiveNumber={true}
                            rules={{ required: "Amount is required" }}
                            hideLabel={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        <button
          type="button"
          onClick={addExemption}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
        >
          <MdAdd className="w-4 h-4" />
          Add an Investment
        </button>
      </div>
    </div>
  );
};

export default ExemptionsForm;